import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuilderComponent, builder, Builder } from '@builder.io/react';
import { BUILDER_API_KEY, BUILDER_INVESTOR_PAGE_URL } from '../../config/builder';
import { stripHiddenBlocks, type BuilderContent } from '../../utils/builderContent';
import { interceptBuilderLinks } from '../../utils/builderLinkRewrites';
import {
  hasInvestorPricingReturn,
  watchInvestorPricingReturn,
} from '../../utils/investorPricingReturnScroll';
import BuilderAccordion from './BuilderAccordion';

const Spinner = () => (
  <div className="flex items-center justify-center p-8 min-h-screen bg-white">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600" />
  </div>
);

interface BuilderPageProps {
  /** Builder URL path targeting (e.g. `/startups`). */
  urlPath?: string;
  /** Fetch a specific published entry by id (overrides urlPath lookup). */
  contentId?: string;
  model?: string;
  customData?: Record<string, unknown>;
  /** Builder block element ids to hide (e.g. embedded nav duplicated by site header). */
  hiddenBlockIds?: readonly string[];
}

export const BuilderPage = ({
  urlPath,
  contentId,
  model = 'page',
  customData,
  hiddenBlockIds,
}: BuilderPageProps) => {
  const [content, setContent] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initDone = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    return interceptBuilderLinks(containerRef.current, navigate);
  }, [navigate, content]);

  useEffect(() => {
    if (urlPath !== BUILDER_INVESTOR_PAGE_URL || !content || !hasInvestorPricingReturn()) {
      return;
    }
    return watchInvestorPricingReturn();
  }, [content, urlPath]);

  useEffect(() => {
    let cancelled = false;

    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!initDone.current) {
          builder.init(BUILDER_API_KEY);

          Builder.registerComponent(BuilderAccordion, {
            name: 'Builder:Accordion',
            inputs: [{ name: 'items', type: 'list' }],
          });

          initDone.current = true;
        }

        const builderContent = contentId
          ? await builder.get(model, { entry: contentId }).promise()
          : await builder
              .get(model, {
                url: urlPath,
                userAttributes: { urlPath },
              })
              .promise();

        // Entry-by-id can return drafts; only render published content.
        const status = (builderContent as { published?: string } | null)?.published;
        const isDraftOrArchived = status === 'draft' || status === 'archived';
        const usableContent = isDraftOrArchived ? null : builderContent;

        const preparedContent =
          hiddenBlockIds?.length && usableContent
            ? stripHiddenBlocks(usableContent as BuilderContent, hiddenBlockIds)
            : usableContent;

        if (!cancelled) setContent(preparedContent);
      } catch (err) {
        console.error('Error fetching Builder content:', err);
        if (!cancelled) setError('Failed to load content');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchContent();
    return () => {
      cancelled = true;
    };
  }, [urlPath, contentId, model, hiddenBlockIds]);

  if (loading) return <Spinner />;

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">{error}</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
          No content found for {contentId || urlPath}. Make sure it&apos;s published in Builder.io.
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-white">
      <BuilderComponent model={model} content={content} data={customData} inlineContent />
    </div>
  );
};

export default BuilderPage;
